<template>
  <div class="c-cartdrawer__wrapper" id="cart" tabindex="0">
    <div class="c-cartdrawer__header">
      <div class="level is-mobile">
        <div class="level-left">
          <div name="cartTitle" class="level-item is-size-4">{{ $t('general.your-cart') }}</div>
        </div>
        <div class="level-right">
          <b-button id="cart-close" type="is-light" icon-right="chevron-right" @click="handleToggleDrawer" @keydown.tab="handleTab">
            <span class="is-sr-only">{{ $t('general.cart') }}</span>
            {{ $t('general.close') }}</b-button
          >
        </div>
      </div>
    </div>
    <div class="c-cartdrawer__items has-background-light">
      <div v-if="itemCount == 0" class="notification is-info is-size-5 has-text-centered u-p2 has-text-white has-text-weight-semibold" role="alert" aria-live="polite">
        <b-icon icon="shopping-cart" size="is-large"></b-icon>
        <br />{{ $t('notices.cart-empty') }}
      </div>
      <Separator />
      <div class="u-p075" v-if="itemCount > 0">
        <CartDisplayItem v-for="(item, idx) in items" :key="item.id" :item="item" :index="idx + 1" />
      </div>
    </div>
    <div class="c-cartdrawer__calculations has-text-weight-semibold u-p1">
      <div class="level is-size-5 is-size-6-mobile is-mobile is-marginless" v-if="subtotal !== total">
        <div class="level-left">
          <div class="level-item">{{ $t('general.running-sum') }}</div>
        </div>
        <div class="level-right has-text-right">
          <div class="level-item">{{ currency | currency }} {{ subtotal | amount }}</div>
        </div>
      </div>
      <div v-if="!reduction" class="level is-size-5 is-size-6-mobile cart-promotion-wrapper">
        <div class="level-left has-text-right-mobile">{{ $t('general.promotion-code') }}</div>
        <div class="level-right is-marginless">
          <!-- translate how? Message -->
          <b-field :type="{'is-danger': invalidPromotion && promoCode}" :message="[{'Promotioncode ungültig!': invalidPromotion && promoCode}]" class="cart-promotion">
            <b-field :label="'Promotioncode'" custom-class="is-sr-only" :label-for="'cart-promotioncode'">
              <b-input id="cart-promotioncode" placeholder="Promotioncode" @input="promotionCodeInput" v-model="promoCode" expanded type="text"></b-input>
            </b-field>
            <p class="control">
              <button class="button is-dark" @click="applyPromotionCode">
                <span class="is-sr-only">Promotioncode</span>
                {{ $t('general.redeem') }}
              </button>
            </p>
          </b-field>
        </div>
      </div>
      <div v-if="reduction" class="level is-size-5 is-size-6-mobile is-mobile is-marginless u-is-vend">
        <div class="level-left">
          <div class="level-item">
            <div>
              <p>{{ $t('general.less-abrev') }} {{ $t('general.promotion-code') }}</p>
              <small class="is-italic is-size-7">{{ reduction }}</small>
            </div>
          </div>
        </div>
        <div class="level-right has-text-right">
          <div class="level-item">{{ currency | currency }} -{{ totalReduction | amount }}</div>
        </div>
        <b-button type="is-text" class="is-size-7" @click="removeReduction">{{ $t('general.remove') }}</b-button>
      </div>

      <div v-if="shipping && shipping > 0" class="level is-size-5 is-size-6-mobile is-mobile is-marginless">
        <div class="level-left">
          <div class="level-item">{{ $t('general.shipping') }}</div>
        </div>
        <div class="level-right has-text-right">
          <div class="level-item">{{ currency | currency }} {{ shipping | amount }}</div>
        </div>
      </div>

      <div v-if="fees && fees.length > 0" class="level is-size-5 is-size-6-mobile is-mobile is-marginless">
        <div class="level-left">
          <div class="level-item">{{ $t('general.fees') }}</div>
        </div>
        <div class="level-right has-text-right">
          <div class="level-item">{{ currency | currency }} {{ 0 | amount }}</div>
        </div>
      </div>

      <div v-if="prepaid && prepaid.length > 0" class="level is-size-5 is-size-6-mobile is-mobile is-marginless">
        <div class="level-left">
          <div class="level-item">{{ $t('general.credit') }}</div>
        </div>
        <div class="level-right has-text-right">
          <div class="level-item" v-for="pp of prepaid" :key="pp.id">
            <button class="delete" v-if="pp.remove" @click="removeGiftCard(pp.remove)"></button>
            <small>{{ pp.name }}&nbsp;</small>
            <strong>-</strong>{{ currency | currency }} {{ pp.amount | amount }}
          </div>
        </div>
      </div>

      <Separator />
      <div class="level is-size-3 is-size-4-mobile is-mobile is-marginless">
        <div class="level-left">
          <div class="level-item">{{ $t('general.total') }}</div>
        </div>
        <div class="level-right has-text-right" v-if="subtotal > 0">
          <div class="level-item">{{ currency | currency }} {{ total | amount }}</div>
        </div>
      </div>
    </div>
    <div class="c-cartdrawer__footer">
      <b-button id="cart-button" :disabled="subtotal == 0" @keydown.tab="focusElement('cart')" type="is-primary" class="is-uppercase is-size-4 is-block u-w100 u-no-radius" name="checkOut" @click="addToCart">{{ $t('general.start-checkout') }}</b-button>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import Separator from '@/components/Separator'
import CartDisplayItem from '@/components/CartDisplayItem'
import focusMixin from '@/mixins/focusMixin'

export default {
  name: 'CartDisplay',
  components: {
    Separator,
    CartDisplayItem,
  },
  mixins: [focusMixin],
  computed: {
    ...mapGetters({
      items: 'productsInCart',
      itemCount: 'itemCount',
      currency: 'cartCurrency',
      reduction: 'activeReduction',
      invalidPromotion: 'invalidPromotion',
      total: 'cartTotal',
      subtotal: 'cartSubTotal',
      shipping: 'cartShipping',
      fees: 'cartFees',
      prepaid: 'cartPrepaid',
      totalReduction: 'cartDiscount',
      active: 'drawer',
    }),
  },
  data() {
    return {
      promoCode: '',
    }
  },
  mounted() {
    this.$store.dispatch('refreshCart')
    window.addEventListener('keydown', this.handleEsc)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleEsc)
  },
  methods: {
    ...mapActions(['removeGiftCard']),
    handleToggleDrawer() {
      this.$store.dispatch('hideDrawer')

      let navigation = window.matchMedia('(max-width: 1023px)').matches ? document.querySelector('#nav-cart-mobile') : document.querySelector('#nav-cart')
      console.log('navigation', navigation)
      if (navigation) {
        this.$nextTick(() => {
          navigation.focus()
        })
      }
    },
    removeReduction() {
      this.$store.dispatch('removeReduction')
    },
    addToCart() {
      this.$store.dispatch('hideDrawer')
      this.$eventDispatcher.dispatch('checkout_clicked', this.items)
      this.focusElement('main')
      //avoid double navigation
      if (this.$route.name !== 'checkout') {
        this.$router.push({name: 'checkout'})
      }
    },
    applyPromotionCode() {
      if (this.promoCode && this.promoCode.length > 0) {
        this.$store.dispatch('applyReduction', this.promoCode)
      }
    },
    promotionCodeInput() {
      if (this.promoCode && this.invalidPromotion) {
        this.$store.dispatch('clearInvalidReduction')
      }
    },
    handleEsc(event) {
      if (event.key === 'Escape' && this.active) {
        this.handleToggleDrawer()
      }
    },
    handleTab(event) {
      if (event.shiftKey) {
        this.focusElement('cart-button')
      }
    },
  },
}
</script>

<style lang="scss">
.cart-promotion-wrapper {
  align-items: flex-start;
}

.cart-promotion {
  flex-wrap: wrap;
  .help {
    width: 100% !important;
  }
}
</style>
